import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import type { ApiError } from '@aithora/shared';
import { ErrorNote } from '../ErrorNote';
import { useReadOnlyReason } from './ReadOnly';
import { sx } from './designStyles';

/** The design's reason field (Oversight approve, T4858; the height suits a dialog). */
const REASON_INPUT = 'box-sizing:border-box;width:100%;min-height:34px;padding:0 12px;border:1px solid rgba(34,39,46,.18);border-radius:8px;background:#FBF8F1;font-size:13px;color:#22272E;font-family:inherit;';

export interface ConfirmReason {
  /** The field's accessible name (and its label when `showLabel`). Default "Reason". */
  label?: string;
  /** Design: "Why is this safe to approve? (required)". */
  placeholder?: string;
  /** Confirm stays off until something is typed. Default true. */
  required?: boolean;
  /** Draw the label above the field (the design's fields carry only a placeholder). */
  showLabel?: boolean;
  /** The field's starting text (an edit of a value already recorded). Default empty. */
  initial?: string;
}

/**
 * The confirm-first pattern. Nothing that changes the world is sent on the first click:
 * the dialog says what will happen, and only Confirm sends the request, with
 * `confirm: true` added to the body (the back end refuses these without it; see
 * v3/settings/change-log.ts). Escape and Cancel close it without sending anything.
 * With `reason`, the dialog asks why, and onConfirm receives the trimmed text.
 */
export function ConfirmDialog({
  title,
  body,
  confirmLabel = 'Confirm',
  danger,
  reason,
  onConfirm,
  onClose,
}: {
  title: string;
  body: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  reason?: ConfirmReason;
  /** The caller adds confirm:true via withConfirm(). Receives the reason ('' without one). Throw to show the error. */
  onConfirm: (reason: string) => Promise<void>;
  onClose: () => void;
}) {
  const id = useId();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [why, setWhy] = useState(reason?.initial ?? '');
  const cancelRef = useRef<HTMLButtonElement>(null);
  const reasonRef = useRef<HTMLInputElement>(null);
  const roReason = useReadOnlyReason();
  const readOnly = roReason !== null;
  // The latest onClose, so a parent re-rendering with a new function neither re-runs the
  // focus below nor leaves Escape calling a stale one.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  // Focus once, when the dialog opens: the reason field when there is one, else Cancel.
  const hasReason = !!reason;
  useEffect(() => {
    (hasReason ? reasonRef.current : cancelRef.current)?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const needWhy = !!reason && reason.required !== false && !why.trim();
  const go = async () => {
    if (needWhy) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm(why.trim());
      onClose();
    } catch (e) {
      setError(e as ApiError);
      setBusy(false);
    }
  };

  const reasonLabel = reason?.label ?? 'Reason';
  return (
    <div className="dialog__scrim" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="dialog" role="alertdialog" aria-modal="true" aria-labelledby={`${id}-t`} aria-describedby={`${id}-b`}>
        <h2 className="dialog__title" id={`${id}-t`}>
          {title}
        </h2>
        <div className="dialog__body" id={`${id}-b`}>
          {body}
        </div>
        {reason && (
          <div style={sx('margin-top:11px;')}>
            {reason.showLabel && (
              <label className="field__label" htmlFor={`${id}-r`}>
                {reasonLabel}
              </label>
            )}
            <input
              ref={reasonRef}
              id={`${id}-r`}
              type="text"
              aria-label={reason.showLabel ? undefined : reasonLabel}
              aria-required={reason.required !== false}
              value={why}
              placeholder={reason.placeholder}
              onChange={(e) => setWhy(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void go();
                }
              }}
              style={sx(REASON_INPUT)}
            />
          </div>
        )}
        {error && <ErrorNote error={error} />}
        <div className="dialog__actions">
          <button ref={cancelRef} type="button" className="btn btn--secondary btn--sm" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={`btn btn--sm ${danger ? 'btn--danger' : 'btn--primary'}`}
            onClick={go}
            disabled={busy || readOnly || needWhy}
            title={readOnly ? roReason : needWhy ? `${reasonLabel} is required` : undefined}
          >
            {busy ? 'Sending…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Add the confirmation the back end asks for to a request body. */
export function withConfirm<B extends object>(body: B): B & { confirm: true } {
  return { ...body, confirm: true as const };
}
